import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useUser } from '../../context/UserContext';
import './UsersScreen.css';

const ROLES = ['ROLE_ADMIN', 'ROLE_USER', 'ROLE_VIEWER'];
const USER_TYPES = ['ORGANIZACION', 'INDIVIDUAL', 'VIEWER'];

const emptyCreateForm = {
    fullName: '',
    email: '',
    password: '',
    userType: 'INDIVIDUAL',
    clientName: '',
    clientId: '',
    role: 'ROLE_USER',
    imageLimit: '',
    organizationLogo: null,
};

const emptyEditForm = {
    fullName: '',
    email: '',
    userType: 'INDIVIDUAL',
    clientId: '',
    role: 'ROLE_USER',
    active: true,
};

export default function UsersScreen() {
    const { user: currentUser } = useUser();
    const [users, setUsers] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);

    // Create modal
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createForm, setCreateForm] = useState(emptyCreateForm);
    const [createSaving, setCreateSaving] = useState(false);
    const [createError, setCreateError] = useState('');

    // Edit modal
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [editForm, setEditForm] = useState(emptyEditForm);
    const [editSaving, setEditSaving] = useState(false);
    const [editError, setEditError] = useState('');
    const [passwordForm, setPasswordForm] = useState({ newPassword: '', confirmPassword: '' });
    const [passwordSaving, setPasswordSaving] = useState(false);
    const [passwordMessage, setPasswordMessage] = useState(null);

    // Delete confirm
    const [deletingUser, setDeletingUser] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const [success, setSuccess] = useState('');
    const [welcomeWarning, setWelcomeWarning] = useState('');

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const res = await axios.get('/api/admin/users');
            setUsers(res.data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const fetchClients = async () => {
        try {
            const res = await axios.get('/api/clients');
            setClients(Array.isArray(res.data) ? res.data : []);
        } catch (e) {
            console.error(e);
            setClients([]);
        }
    };

    useEffect(() => {
        fetchUsers();
        fetchClients();
    }, []);

    /* ── Create ── */
    const handleCreateChange = (e) => {
        const { name, value, files } = e.target;
        setCreateForm(prev => {
            const next = { ...prev, [name]: name === 'organizationLogo' ? files?.[0] || null : value };
            if (name === 'userType' && value === 'VIEWER') {
                next.role = 'ROLE_VIEWER';
                next.imageLimit = '';
                next.clientName = '';
            }
            if (name === 'role' && value === 'ROLE_VIEWER') {
                next.userType = 'VIEWER';
                next.imageLimit = '';
            }
            return next;
        });
        setCreateError('');
    };

    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        setCreateSaving(true);
        setWelcomeWarning('');
        setCreateError('');
        try {
            const payload = {
                ...createForm,
                clientId: createForm.userType === 'VIEWER' ? createForm.clientId : null,
                clientName: createForm.userType === 'ORGANIZACION' ? createForm.clientName : '',
                imageLimit: createForm.userType === 'VIEWER'
                    ? 0
                    : createForm.imageLimit !== '' ? parseInt(createForm.imageLimit, 10) : null,
            };
            const formData = new FormData();
            Object.entries(payload).forEach(([key, value]) => {
                if (key !== 'organizationLogo' && value !== null && value !== '') {
                    formData.append(key, value);
                }
            });
            if (createForm.organizationLogo) {
                formData.append('logo', createForm.organizationLogo);
            }
            const { data } = await axios.post('/api/admin/users', formData);
            setSuccess(data.welcomeEmailSent ? 'User created. Welcome email submitted for delivery.' : 'User created successfully.');
            if (data.welcomeEmailSent === false) setWelcomeWarning('The account was created, but its welcome email could not be sent. Check RESEND_API_KEY and MAIL_FROM in the backend and verify the sender domain. Do not recreate the account.');
            setCreateForm(emptyCreateForm);
            setShowCreateModal(false);
            fetchUsers();
        } catch (err) {
            setCreateError(err.response?.data?.error || 'Could not create the user.');
        } finally {
            setCreateSaving(false);
        }
    };

    /* ── Edit ── */
    const openEditModal = (user) => {
        setEditingUser(user);
        setEditForm({
            fullName: user.fullName || '',
            email: user.email || '',
            userType: user.userType || 'INDIVIDUAL',
            clientId: user.clientId || '',
            role: user.roles?.[0] || 'ROLE_USER',
            active: user.active,
        });
        setEditError('');
        setPasswordForm({ newPassword: '', confirmPassword: '' });
        setPasswordMessage(null);
        setShowEditModal(true);
    };

    const handleEditChange = (e) => {
        const value = e.target.name === 'active' ? e.target.value === 'true' : e.target.value;
        setEditForm(prev => {
            const next = { ...prev, [e.target.name]: value };
            if (e.target.name === 'userType' && value === 'VIEWER') {
                next.role = 'ROLE_VIEWER';
            }
            if (e.target.name === 'role' && value === 'ROLE_VIEWER') {
                next.userType = 'VIEWER';
            }
            return next;
        });
        setEditError('');
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        setEditSaving(true);
        setEditError('');
        try {
            await axios.put(`/api/admin/users/${editingUser.id}`, editForm);
            setSuccess('Usuario actualizado exitosamente.');
            setShowEditModal(false);
            setEditingUser(null);
            fetchUsers();
        } catch (err) {
            setEditError(err.response?.data?.error || 'Could not update the user.');
        } finally {
            setEditSaving(false);
        }
    };

    const handlePasswordChange = (e) => {
        setPasswordForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
        setPasswordMessage(null);
    };

    const handlePasswordReset = async () => {
        if (!editingUser) return;
        if (passwordForm.newPassword.length < 8) {
            setPasswordMessage({ type: 'error', text: 'Password must be at least 8 characters.' });
            return;
        }
        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            setPasswordMessage({ type: 'error', text: 'Passwords do not match.' });
            return;
        }

        setPasswordSaving(true);
        setPasswordMessage(null);
        try {
            await axios.put(`/api/admin/users/${editingUser.id}/password`, {
                newPassword: passwordForm.newPassword,
            });
            setPasswordForm({ newPassword: '', confirmPassword: '' });
            setPasswordMessage({ type: 'success', text: 'Password updated successfully.' });
        } catch (err) {
            setPasswordMessage({
                type: 'error',
                text: err.response?.data?.error || 'Error updating password.',
            });
        } finally {
            setPasswordSaving(false);
        }
    };

    /* ── Delete ── */
    const handleDelete = async () => {
        if (!deletingUser) return;
        setDeleteLoading(true);
        try {
            await axios.delete(`/api/admin/users/${deletingUser.id}`);
            setSuccess('Usuario eliminado exitosamente.');
            setDeletingUser(null);
            fetchUsers();
        } catch (err) {
            setSuccess('');
            alert(err.response?.data?.error || 'Could not delete the user.');
        } finally {
            setDeleteLoading(false);
        }
    };

    const formatDate = (d) => {
        if (!d) return '-';
        return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    };

    return (
        <div className="users-screen">
            <div className="users-header">
                <h1 className="users-title">Users</h1>
                <button
                    className="users-new-btn"
                    onClick={() => { setShowCreateModal(true); setCreateError(''); setSuccess(''); }}
                >
                    + New User
                </button>
            </div>

            {success && <p className="users-success" role="status">{success}</p>}
            {welcomeWarning && <p className="users-form-error" role="alert">{welcomeWarning}</p>}

            {loading ? (
                <div className="users-loading">Loading users…</div>
            ) : (
                <div className="users-table-wrap">
                    <table className="users-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Type</th>
                                <th>Roles</th>
                                <th>Status</th>
                                <th>Image Limit</th>
                                <th>Created</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.length === 0 && (
                                <tr><td colSpan={8} className="users-empty">No users found.</td></tr>
                            )}
                            {users.map(u => (
                                <tr key={u.id}>
                                    <td>{u.fullName}</td>
                                    <td>{u.email}</td>
                                    <td>
                                        <span className="users-badge type">{u.userType}</span>
                                        {u.clientName && <span className="users-badge">{u.clientName}</span>}
                                    </td>
                                    <td>
                                        {u.roles?.length > 0
                                            ? u.roles.map(r => (
                                                <span key={r} className={`users-badge role ${r === 'ROLE_ADMIN' ? 'admin' : ''}`}>{r}</span>
                                            ))
                                            : <span className="users-badge">—</span>
                                        }
                                    </td>
                                    <td>
                                        <span className={`users-badge ${u.active ? 'active' : 'inactive'}`}>
                                            {u.active ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td>
                                        {u.testUser
                                            ? <span className="users-badge test">{u.imageLimit ?? '∞'} imgs</span>
                                            : <span className="users-badge">—</span>
                                        }
                                    </td>
                                    <td>{formatDate(u.createdAt)}</td>
                                    <td className="users-actions-cell">
                                        <button
                                            className="users-action-btn edit"
                                            title="Edit user"
                                            onClick={() => { setSuccess(''); openEditModal(u); }}
                                        >
                                            ✏️
                                        </button>
                                        <button
                                            className="users-action-btn delete"
                                            title={u.id === currentUser?.id ? 'Cannot delete your own account' : 'Delete user'}
                                            disabled={u.id === currentUser?.id}
                                            onClick={() => { setSuccess(''); setDeletingUser(u); }}
                                        >
                                            🗑️
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* ── Create Modal ── */}
            {showCreateModal && (
                <div className="users-modal-overlay" onClick={() => setShowCreateModal(false)}>
                    <div className="users-modal" onClick={e => e.stopPropagation()}>
                        <div className="users-modal-header">
                            <h2>New User</h2>
                            <button className="users-modal-close" onClick={() => setShowCreateModal(false)}>✕</button>
                        </div>

                        <form onSubmit={handleCreateSubmit} className="users-form">
                            <label>Full Name
                                <input name="fullName" value={createForm.fullName} onChange={handleCreateChange} required placeholder="John Doe" />
                            </label>

                            <label>Email
                                <input name="email" type="email" value={createForm.email} onChange={handleCreateChange} required placeholder="john@example.com" />
                            </label>

                            <label>Password
                                <input name="password" type="password" value={createForm.password} onChange={handleCreateChange} required placeholder="Min. 8 characters" minLength={8} />
                            </label>

                            <label>User Type
                                <select name="userType" value={createForm.userType} onChange={handleCreateChange}>
                                    {USER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </label>

                            {createForm.userType === 'ORGANIZACION' && (
                                <>
                                    <label>Organization Name
                                        <input name="clientName" value={createForm.clientName} onChange={handleCreateChange} placeholder="Acme Corp" required />
                                    </label>
                                    <label>Organization Logo
                                        <input name="organizationLogo" type="file" accept="image/*" onChange={handleCreateChange} />
                                    </label>
                                </>
                            )}

                            {createForm.userType === 'VIEWER' && (
                                <label>Client
                                    <select name="clientId" value={createForm.clientId} onChange={handleCreateChange} required>
                                        <option value="">Select client</option>
                                        {clients.map(client => (
                                            <option key={client.id} value={client.id}>{client.name}</option>
                                        ))}
                                    </select>
                                </label>
                            )}

                            <label>Role
                                <select name="role" value={createForm.role} onChange={handleCreateChange}>
                                    {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                                </select>
                            </label>

                            {createForm.userType !== 'VIEWER' && (
                                <label>Image Limit
                                    <input
                                        name="imageLimit"
                                        type="number"
                                        min="1"
                                        value={createForm.imageLimit}
                                        onChange={handleCreateChange}
                                        placeholder="Leave empty for no limit (test user)"
                                    />
                                </label>
                            )}

                            {createError && <p className="users-form-error">{createError}</p>}

                            <div className="users-form-actions">
                                <button type="button" className="users-cancel-btn" onClick={() => setShowCreateModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="users-submit-btn" disabled={createSaving}>
                                    {createSaving ? <span className="users-spinner" /> : 'Create User'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ── Edit Modal ── */}
            {showEditModal && editingUser && (
                <div className="users-modal-overlay" onClick={() => setShowEditModal(false)}>
                    <div className="users-modal" onClick={e => e.stopPropagation()}>
                        <div className="users-modal-header">
                            <h2>Edit User</h2>
                            <button className="users-modal-close" onClick={() => setShowEditModal(false)}>✕</button>
                        </div>

                        <form onSubmit={handleEditSubmit} className="users-form">
                            <label>Full Name
                                <input name="fullName" value={editForm.fullName} onChange={handleEditChange} required placeholder="John Doe" />
                            </label>

                            <label>Email
                                <input name="email" type="email" value={editForm.email} onChange={handleEditChange} required placeholder="john@example.com" />
                            </label>

                            <label>User Type
                                <select name="userType" value={editForm.userType} onChange={handleEditChange}>
                                    {USER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </label>

                            {editForm.userType === 'VIEWER' && (
                                <label>Client
                                    <select name="clientId" value={editForm.clientId} onChange={handleEditChange} required>
                                        <option value="">Select client</option>
                                        {clients.map(client => (
                                            <option key={client.id} value={client.id}>{client.name}</option>
                                        ))}
                                    </select>
                                </label>
                            )}

                            <label>Role
                                <select name="role" value={editForm.role} onChange={handleEditChange}>
                                    {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                                </select>
                            </label>

                            <label>Status
                                <select name="active" value={String(editForm.active)} onChange={handleEditChange}>
                                    <option value="true">Active</option>
                                    <option value="false">Inactive</option>
                                </select>
                            </label>

                            {editError && <p className="users-form-error">{editError}</p>}

                            <div className="users-password-reset">
                                <div>
                                    <h3>Reset Password</h3>
                                    <p>Set a new password for this user.</p>
                                </div>
                                <label>New Password
                                    <input
                                        name="newPassword"
                                        type="password"
                                        value={passwordForm.newPassword}
                                        onChange={handlePasswordChange}
                                        placeholder="Min. 8 characters"
                                        minLength={8}
                                    />
                                </label>
                                <label>Confirm Password
                                    <input
                                        name="confirmPassword"
                                        type="password"
                                        value={passwordForm.confirmPassword}
                                        onChange={handlePasswordChange}
                                        placeholder="Repeat new password"
                                        minLength={8}
                                    />
                                </label>
                                {passwordMessage && (
                                    <p className={passwordMessage.type === 'success' ? 'users-form-success' : 'users-form-error'}>
                                        {passwordMessage.text}
                                    </p>
                                )}
                                <button
                                    type="button"
                                    className="users-reset-password-btn"
                                    onClick={handlePasswordReset}
                                    disabled={passwordSaving}
                                >
                                    {passwordSaving ? <span className="users-spinner" /> : 'Update Password'}
                                </button>
                            </div>

                            <div className="users-form-actions">
                                <button type="button" className="users-cancel-btn" onClick={() => setShowEditModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="users-submit-btn" disabled={editSaving}>
                                    {editSaving ? <span className="users-spinner" /> : 'Save Changes'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ── Delete Confirm ── */}
            {deletingUser && (
                <div className="users-modal-overlay" onClick={() => setDeletingUser(null)}>
                    <div className="users-modal users-confirm" onClick={e => e.stopPropagation()}>
                        <div className="users-modal-header danger">
                            <h2>Delete User</h2>
                            <button className="users-modal-close" onClick={() => setDeletingUser(null)}>✕</button>
                        </div>
                        <div className="users-confirm-body">
                            <p>Are you sure you want to delete <strong>{deletingUser.fullName}</strong>?</p>
                            <p className="users-confirm-sub">This action cannot be undone.</p>
                            <div className="users-form-actions">
                                <button className="users-cancel-btn" onClick={() => setDeletingUser(null)}>
                                    Cancel
                                </button>
                                <button className="users-delete-confirm-btn" onClick={handleDelete} disabled={deleteLoading}>
                                    {deleteLoading ? <span className="users-spinner" /> : 'Delete'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
